import axios from "axios";
import type { OverviewReflector, OverviewCluster, BgpSession } from "./OverviewData";

const API_URL = process.env.REACT_APP_API_URL || "";

export type OverviewType = "reflectors" | "clusters" | "bgp";

export interface OverviewCounters {
    reflectors: { total: number; online: number; offline: number };
    clusters: { total: number; online: number; degraded: number; offline: number };
    bgp: { total: number; established: number; down: number; idle?: number; noData?: number };
}

export interface OverviewResponse<T> {
    counters: OverviewCounters;
    items: T[];
    pagination?: { total: number; limit: number; offset: number };
}

export function fetchOverview(
    token: string,
    tenantId: number,
    type: OverviewType,
    limit = 10,
    page = 1,
    sortOrder: "asc" | "desc" = "desc",
) {
    const params = new URLSearchParams({
        type,
        limit: String(limit),
        page: String(page),
        sortOrder,
        tenantId: String(tenantId),
    });
    return axios.get(`${API_URL}/v2/reflector/overview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/* ------------------------------------------------------------------ */
/*  Shapers — map API items → table row types                          */
/* ------------------------------------------------------------------ */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function shapeReflectors(items: any[]): OverviewReflector[] {
    return items.map((item) => ({
        id: String(item.id ?? ""),
        name: item.name || "",
        description: item.description || "",
        subId: item.subId || "",
        status: item.status || "Offline",
        cloud: item.cloud ? item.cloud.toUpperCase() : "",
        region: item.region || "",
        version: item.version || "-",
    }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function shapeClusters(items: any[]): OverviewCluster[] {
    return items.map((item) => ({
        id: String(item.id ?? ""),
        name: item.name || `CL-${item.id}`,
        status: item.status || (item.enable ? "Online" : "Offline"),
        cloud: item.cloud ? item.cloud.toUpperCase() : "",
        region: item.region || "",
        description: item.description || "",
    }));
}

/**
 * Derive BFD boolean + session states from overview payload.
 *
 * Overview `/v2/reflector/overview` sends `bfdStatus` as a config summary:
 *   "Inactive" → BFD off   "Active" → BFD on (no session telemetry).
 * BGP Details neighbors API sends `BFD` boolean + `bfdLocalState` / `bfdRemoteState`.
 *
 * Both are normalised to { BFD, bfdLocalState, bfdRemoteState } so the
 * column renderer can apply the same rule everywhere:
 *   BFD === false → "Inactive"
 *   BFD === true  → buildBfdLabel(localState, remoteState, changedAt)
 */
function normalizeOverviewBfdFields(item: {
    BFD?: boolean;
    bfd?: boolean;
    bfdEnabled?: boolean;
    bfdStatus?: string | null;
    bfdLocalState?: string | null;
    bfdRemoteState?: string | null;
}): { BFD: boolean; bfdLocalState: string | null; bfdRemoteState: string | null } {
    const explicitEnabled = Boolean(item.BFD ?? item.bfd ?? item.bfdEnabled);
    const localFromApi = item.bfdLocalState?.trim() || null;
    const remoteFromApi = item.bfdRemoteState?.trim() || null;

    if (localFromApi && remoteFromApi) {
        return { BFD: true, bfdLocalState: localFromApi, bfdRemoteState: remoteFromApi };
    }

    const summary = item.bfdStatus?.trim() || null;
    if (!summary) {
        return { BFD: explicitEnabled, bfdLocalState: null, bfdRemoteState: null };
    }

    if (summary.includes("/")) {
        const [local, remote] = summary.split("/").map((s) => s.trim());
        if (local && remote) {
            return { BFD: true, bfdLocalState: local, bfdRemoteState: remote };
        }
    }

    const upper = summary.toUpperCase();
    if (upper === "INACTIVE" || upper === "NOT-ACTIVE" || upper === "DISABLED") {
        return { BFD: false, bfdLocalState: null, bfdRemoteState: null };
    }
    if (upper === "ACTIVE") {
        return { BFD: true, bfdLocalState: null, bfdRemoteState: null };
    }

    return { BFD: explicitEnabled, bfdLocalState: null, bfdRemoteState: null };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function shapeBgpSessions(items: any[]): BgpSession[] {
    return items.map((item) => {
        const ri = item.reflectorInstance;
        const bfd = normalizeOverviewBfdFields(item);

        return {
            id: String(item.id ?? item.neighborAddress ?? ""),
            reflectorInstanceId: String(ri?.id ?? ""),
            reflectorName: ri?.name || item.reflectorName || "",
            reflectorDescription: ri?.description || item.reflectorDescription || "",
            neighborAddress: item.neighborAddress || "",
            neighborDescription: item.description || item.neighborDescription || "",
            operatingMode: item.operatingMode || "Enterprise",
            status: item.status ?? item.bgpStatus ?? "",
            statusChangedAt: item.statusChangedAt ?? null,
            localAs: item.localAs ?? 0,
            peerAs: item.peerAs ?? 0,
            prefixesReceived: item.prefixesReceived ?? 0,
            prefixesAdvertised: item.prefixesAdvertised ?? 0,
            BFD: bfd.BFD,
            bfdLocalState: bfd.bfdLocalState,
            bfdRemoteState: bfd.bfdRemoteState,
            bfdStatusChangedAt: item.bfdStatusChangedAt ?? null,
            // Owning-reflector liveness (null when the backend does not send
            // it yet): lets the table replace frozen session data with the
            // "No data — reflector down" treatment.
            reflectorOnline: item.reflectorOnline ?? null,
            reflectorLastUpdate: item.reflectorLastUpdate ?? null,
        };
    });
}
